/**
 * marketHours.js
 * Shared utility — reliable ET market hours check using Intl.DateTimeFormat.
 * Avoids the fragile `new Date(toLocaleString())` pattern which can produce
 * "Invalid Date" in Firefox and other environments.
 *
 * isMarketHours()  → true during 9:30–16:00 ET Mon–Fri
 * isMarketOpen()   → same as isMarketHours() (alias)
 * useMarketOpen()  → React hook, updates every 30s, always current
 */

import { useState, useEffect } from 'react';

// Extract ET hour+minute from any Date using Intl — works in all browsers
function etMinutesNow(date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  const weekday = p.weekday; // 'Mon', 'Tue', ... 'Sat', 'Sun'
  const hour    = Number(p.hour === '24' ? '0' : p.hour);
  const minute  = Number(p.minute);

  if (weekday === 'Sat' || weekday === 'Sun') return { mins: -1, weekday };
  return { mins: hour * 60 + minute, weekday };
}

export function isMarketHours(date) {
  const { mins } = etMinutesNow(date);
  return mins >= 570 && mins < 960; // 9:30–16:00 ET
}

export function isMarketOpen(date) {
  return isMarketHours(date);
}

// React hook — rechecks every 30 seconds so the UI stays accurate
export function useMarketOpen() {
  const [open, setOpen] = useState(() => isMarketHours());

  useEffect(() => {
    // Recheck immediately, then every 30s
    const tick = () => setOpen(isMarketHours());
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, []);

  return open;
}
